import { useQueryClient } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { SkuRule, SkuRuleApplyScope, SkuRuleRun } from '@nucleus-commerce/api-client/admin';
import { SkuRuleApplyScope as Scopes } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { Spinner } from '@nucleus-commerce/ui/components/spinner';

import { dictionaryKeys } from '../hooks/dictionary-keys';
import { useApplySkuRule } from '../hooks/use-sku-rule-mutations';
import { isRunFinished, useSkuRuleRun } from '../hooks/use-sku-rule-run';
import { useSkuRuleRuns } from '../hooks/use-sku-rules';

const SCOPES: readonly SkuRuleApplyScope[] = [Scopes.missing, Scopes.generated, Scopes.all];

/** The run's counts in one sentence: what changed, numbers used, what was skipped and why. */
function useCountsSentence() {
  const { t } = useTranslation('skuRules');
  return (run: SkuRuleRun): string | null => {
    const counts = run.counts;
    if (!counts) return null;
    const parts = [
      t(run.dryRun ? 'apply.counts.wouldChange' : 'apply.counts.changed', {
        roots: t('apply.counts.roots', { count: counts.roots }),
        variants: t('apply.counts.variants', { count: counts.variants }),
      }),
      t(run.dryRun ? 'apply.counts.numbersWould' : 'apply.counts.numbersWere', {
        count: counts.numbersConsumed,
      }),
    ];
    if (counts.skipped.inUse > 0)
      parts.push(t('apply.counts.inUse', { count: counts.skipped.inUse }));
    if (counts.skipped.manual > 0)
      parts.push(t('apply.counts.manual', { count: counts.skipped.manual }));
    if (counts.skipped.untyped > 0)
      parts.push(t('apply.counts.untyped', { count: counts.skipped.untyped }));
    if (counts.skipped.conflict > 0)
      parts.push(t('apply.counts.conflict', { count: counts.skipped.conflict }));
    return `${parts.join(', ')} (${t('apply.counts.products', { count: counts.products })}).`;
  };
}

function RunStatus({ run }: { run: SkuRuleRun }) {
  const { t } = useTranslation('skuRules');
  const sentence = useCountsSentence();
  const busy = !isRunFinished(run);
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col gap-1 rounded-md border p-3 text-sm"
      data-testid="sku-rule-run-status"
    >
      <div className="flex items-center gap-2">
        {busy ? <Spinner className="size-4" /> : null}
        <span className="font-medium">
          {run.dryRun ? t('apply.dryRunResult') : t('apply.runResult')}
        </span>
        <Badge
          variant={run.status === 'failed' ? 'destructive' : 'secondary'}
          className="font-normal"
        >
          {t(`apply.runs.status.${run.status}`)}
        </Badge>
      </div>
      {run.status === 'failed' ? (
        <p className="text-destructive">{t('apply.failed', { error: run.error ?? '' })}</p>
      ) : run.status === 'done' ? (
        <p>{sentence(run)}</p>
      ) : (
        <p className="text-muted-foreground">{t('apply.inProgress')}</p>
      )}
    </div>
  );
}

function RunHistory({ ruleId }: { ruleId: string }) {
  const { t } = useTranslation('skuRules');
  const format = useFormat();
  const sentence = useCountsSentence();
  const runs = useSkuRuleRuns(ruleId);
  const rows = runs.data ?? [];
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">{t('apply.runs.title')}</h3>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('apply.runs.empty')}</p>
      ) : (
        <ul
          className="flex flex-col divide-y rounded-md border text-sm"
          aria-label={t('apply.runs.title')}
        >
          {rows.slice(0, 5).map((run) => (
            <li key={run.id} className="flex flex-col gap-0.5 px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground text-xs" title={run.createdAt}>
                  {format.relative(run.createdAt)}
                </span>
                <Badge variant="outline" className="font-normal">
                  {t(`apply.scopes.${run.scope}`)}
                </Badge>
                {run.dryRun ? (
                  <Badge variant="outline" className="font-normal">
                    {t('apply.runs.dryRun')}
                  </Badge>
                ) : null}
                <Badge
                  variant={run.status === 'failed' ? 'destructive' : 'secondary'}
                  className="font-normal"
                >
                  {t(`apply.runs.status.${run.status}`)}
                </Badge>
              </div>
              {run.status === 'done' ? (
                <p className="text-muted-foreground text-xs">{sentence(run)}</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DialogBody({ rule }: { rule: SkuRule }) {
  const { t } = useTranslation('skuRules');
  const message = useErrorMessage();
  const queryClient = useQueryClient();
  const apply = useApplySkuRule(rule.id);
  const [scope, setScope] = useState<SkuRuleApplyScope>(Scopes.missing);
  const [includeManual, setIncludeManual] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const tracked = useSkuRuleRun(rule.id, runId);
  const run = tracked.data;
  const busy = apply.isPending || (run !== undefined && !isRunFinished(run));
  const dryRunDone = run?.dryRun === true && run.status === 'done';
  const realRunDone = run?.dryRun === false && run.status === 'done';

  // A finished real run changed products and counters; the rule row and its runs are refetched.
  useEffect(() => {
    if (!run || !isRunFinished(run)) return;
    void queryClient.invalidateQueries({ queryKey: dictionaryKeys.skuRuleRuns(rule.id) });
    if (!run.dryRun) {
      void queryClient.invalidateQueries({ queryKey: dictionaryKeys.skuRule(rule.id) });
      void queryClient.invalidateQueries({ queryKey: dictionaryKeys.skuRules(), exact: true });
    }
  }, [run, queryClient, rule.id]);

  const start = (dryRun: boolean) => {
    apply.mutate(
      { scope, dryRun, ...(scope === Scopes.all ? { includeManual } : {}) },
      { onSuccess: (created) => setRunId(created.id) },
    );
  };

  const needsConfirmation = scope === Scopes.all && !includeManual;

  return (
    <div className="flex flex-col gap-5">
      <fieldset disabled={busy} className="flex flex-col gap-3">
        <legend className="text-sm font-medium">{t('apply.scope')}</legend>
        <RadioGroup
          value={scope}
          onValueChange={(next) => {
            const picked = SCOPES.find((s) => s === next);
            if (picked) setScope(picked);
            setRunId(null);
          }}
        >
          {SCOPES.map((option) => (
            <div key={option} className="flex items-start gap-2">
              <RadioGroupItem value={option} id={`apply-scope-${option}`} className="mt-0.5" />
              <Label
                htmlFor={`apply-scope-${option}`}
                className="flex flex-col gap-0.5 font-normal"
              >
                <span>{t(`apply.scopes.${option}`)}</span>
                <span className="text-muted-foreground text-xs">
                  {t(`apply.scopes.${option}Hint`)}
                </span>
              </Label>
            </div>
          ))}
        </RadioGroup>
        {scope === Scopes.all ? (
          <div className="border-destructive/40 flex items-start gap-2 rounded-md border p-3">
            <Checkbox
              id="apply-include-manual"
              className="mt-0.5"
              checked={includeManual}
              onCheckedChange={(next) => {
                setIncludeManual(next === true);
                setRunId(null);
              }}
            />
            <Label htmlFor="apply-include-manual" className="font-normal">
              {t('apply.includeManual')}
            </Label>
          </div>
        ) : null}
      </fieldset>
      {apply.isError ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{message(apply.error)}</AlertDescription>
        </Alert>
      ) : null}
      {run ? <RunStatus run={run} /> : null}
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          disabled={busy || needsConfirmation}
          onClick={() => start(true)}
        >
          {t('apply.dryRun')}
        </Button>
        <Button
          type="button"
          disabled={busy || needsConfirmation || !dryRunDone}
          onClick={() => start(false)}
        >
          <Play aria-hidden />
          {realRunDone ? t('apply.runAgain') : t('apply.run')}
        </Button>
      </DialogFooter>
      <RunHistory ruleId={rule.id} />
    </div>
  );
}

/**
 * "Apply to existing": pick a scope, dry-run to see the counts in words, then run for real; both
 * poll the run row every 2 s until it is done or failed. The last runs are listed under it.
 */
export function SkuRuleApplySection({ rule }: { rule: SkuRule }) {
  const { t } = useTranslation('skuRules');
  const [open, setOpen] = useState(false);
  return (
    <FormSection title={t('sections.apply')} description={t('sections.applyDescription')}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button type="button" variant="outline" className="w-full">
            <Play aria-hidden />
            {t('apply.open')}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('apply.title', { name: rule.name })}</DialogTitle>
            <DialogDescription>{t('apply.description')}</DialogDescription>
          </DialogHeader>
          {open ? <DialogBody rule={rule} /> : null}
        </DialogContent>
      </Dialog>
    </FormSection>
  );
}
